import { Box, Text, Flex } from "@chakra-ui/react";
import { useColorModeValue } from "../../components/ui/color-mode";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import {
  toggleShowCurrent,
  toggleShowNew,
  toggleShowConfirm,
} from "../../store/securitySlice";
import { changePasswordRequest } from "../../store/userSlice";
import { InputBox } from "../../components/shared/fields";
import ButtonBox from "../../components/shared/buttons/ButtonBox";
import { passwordSchema } from "../../utils/schemas/passwordSchema";
import { darkMode, lightMode } from "../../theme/config";

const redStar = <span style={{ color: "red" }}>*</span>;

const SecurityTab = () => {
  const textColor = useColorModeValue(lightMode.textColor, darkMode.textColor);
  const dispatch = useDispatch();

  const { loading } = useSelector((state) => ({
    loading: state.user.loading,
  }));

  const { showCurrent, showNew, showConfirm } = useSelector(
    (state) => state.security
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(passwordSchema),
  });

  const onSubmit = (data) => {
    dispatch(
      changePasswordRequest({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      })
    );
  };

  return (
    <Box p={4} color={textColor}>
      <Text fontSize="lg" fontWeight="bold" mb={6} color={textColor}>
        Change Password
      </Text>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Current Password */}
        <Box mb={4} position="relative">
          <InputBox
            label={<>Current Password {redStar}</>}
            placeholder="Enter current password"
            register={register("currentPassword")}
            type={showCurrent ? "text" : "password"}
            invalid={!!errors.currentPassword}
            errorText={errors.currentPassword?.message}
          />
          <Box
            position="absolute"
            right="12px"
            top="50%"
            transform="translateY(-50%)"
            cursor="pointer"
            color={textColor}
            onClick={() => dispatch(toggleShowCurrent())}
          >
            {showCurrent ? <FaEyeSlash /> : <FaEye />}
          </Box>
        </Box>

        {/* New Password */}
        <Box mb={4} position="relative">
          <InputBox
            label={<>New Password {redStar}</>}
            placeholder="Enter new password"
            register={register("newPassword")}
            type={showNew ? "text" : "password"}
            invalid={!!errors.newPassword}
            errorText={errors.newPassword?.message}
          />
          <Box
            position="absolute"
            right="12px"
            top="50%"
            transform="translateY(-50%)"
            cursor="pointer"
            color={textColor}
            onClick={() => dispatch(toggleShowNew())}
          >
            {showNew ? <FaEyeSlash /> : <FaEye />}
          </Box>
        </Box>

        {/* Confirm New Password */}
        <Box mb={6} position="relative">
          <InputBox
            label={<>Confirm New Password {redStar}</>}
            placeholder="Confirm new password"
            register={register("confirmPassword")}
            type={showConfirm ? "text" : "password"}
            invalid={!!errors.confirmPassword}
            errorText={errors.confirmPassword?.message}
          />
          <Box
            position="absolute"
            right="12px"
            top="50%"
            transform="translateY(-50%)"
            cursor="pointer"
            color={textColor}
            onClick={() => dispatch(toggleShowConfirm())}
          >
            {showConfirm ? <FaEyeSlash /> : <FaEye />}
          </Box>
        </Box>

        <Flex justify="flex-end">
          <ButtonBox
            name={loading ? "Changing…" : "Change Password"}
            variant="solid"
            colorPalette="blue"
            borderRadius={12}
            type="submit"
            disabled={loading}
          />
        </Flex>
      </form>
    </Box>
  );
};

export default SecurityTab;
